import { i18n } from "@/shared/i18n";
import { isEnvelope, unwrap } from "./envelope";
import { errorFromBody, NetworkError, toApiError } from "./errors";
import { refreshAccessToken, session } from "./session";

const TIMEOUT_MS = 15_000;
/** Endpoints that must never trigger the automatic refresh-and-retry. */
const AUTH_PATHS = ["/api/v1/auth/otp", "/api/v1/auth/refresh", "/api/v1/auth/logout"];

/**
 * The single HTTP entry point for the app. Generated API functions (orval) call it for every
 * request, so cross-cutting behaviour lives here and nowhere else:
 *  - attaches the in-memory access token and the UI language (the backend localizes texts),
 *  - times out stalled requests,
 *  - on 401 refreshes the session once and retries,
 *  - unwraps the company envelope (ADR-0007): returns `response_data` (paginated lists: `{items, meta, links}`),
 *  - turns failed responses into ApiError with the stable `sub_code` as `code`.
 */
export async function httpRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  let response = await send(url, init);

  if (response.status === 401 && !isAuthPath(url) && session.getAccessToken() !== null) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await send(url, init);
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("json")) return (await response.text()) as T;
  const body: unknown = await response.json();
  if (!isEnvelope(body)) return body as T; // machine formats without the envelope (JWKS)
  if (!body.success) throw errorFromBody(response.status, body);
  return unwrap(body) as T;
}

async function send(url: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = session.getAccessToken();
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Accept-Language")) headers.set("Accept-Language", i18n.language);

  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;

  try {
    // Absolute URL: relative URLs are not supported by fetch outside the browser (tests).
    return await fetch(new URL(url, window.location.origin), {
      ...init,
      headers,
      signal,
      credentials: "same-origin",
    });
  } catch (error) {
    if (init.signal?.aborted) throw error; // cancelled by the caller (e.g. TanStack Query)
    throw new NetworkError(error);
  }
}

function isAuthPath(url: string): boolean {
  const path = new URL(url, "http://local").pathname;
  return AUTH_PATHS.some((authPath) => path.startsWith(authPath));
}
